import type { CalendarClickType, DateCalendarProps as Props, YYYYMMDD } from '../../typings'

import { useCallback } from 'react'

import { Month } from '../../components/Month'
import { MonthNavigator } from '../../components/MonthNavigator'
import { useMonthNavigatorProps } from '../../hooks/useMonthNavigatorProps'
import { useMonths } from '../../hooks/useMonths'
import { parseDate } from '../../utils/parseDate'

import { Stack } from '@mui/material'
import { useDateDays } from './useDateDays'
import { useSelectionData } from './useSelectionData'

export function Date(props: Props) {
  const {
    today,
    minDate,
    maxDate,
    dotColor,
    onChange,
    selection,
    disabledDays,
    referenceDate,
    highlightedDays,
    tooltipIntlText,
    setReferenceDate,
    firstNonArchivedDay,
    showNeighboringMonth,
    allowAutoMonthHeight,
    months: monthsNumber,
  } = props

  const months = useMonths(referenceDate, showNeighboringMonth, monthsNumber, minDate, maxDate)

  const selectionData = useSelectionData(selection)

  const daysByMonth = useDateDays({
    selection: selectionData,
    firstNonArchivedDay,
    tooltipIntlText,
    highlightedDays,
    disabledDays,
    months,
    today,
  })

  const monthNavigatorProps = useMonthNavigatorProps({
    referenceDate,
    setReferenceDate,
    minDateAsString: minDate,
    maxDateAsString: maxDate,
    labelFormat: 'month-year',
    months: monthsNumber,
  })

  const handleOnClick = useCallback(
    (day?: YYYYMMDD, type: CalendarClickType = 'click') => {
      if (day) {
        const dayDate = parseDate(day)
        onChange(dayDate, selection, type)
      }
    },
    [onChange, selection],
  )

  return (
    <Stack direction="column" height="auto" alignItems="center" flexShrink={0}>
      <MonthNavigator {...monthNavigatorProps} />
      <Stack direction="row" width="auto">
        {months.map(
          (month, index) =>
            daysByMonth[month.id] && (
              <Month
                index={index}
                key={month.id}
                showDaysOfWeek
                dotColor={dotColor}
                onClick={handleOnClick}
                days={daysByMonth[month.id]}
                allowAutoMonthHeight={allowAutoMonthHeight}
              />
            ),
        )}
      </Stack>
    </Stack>
  )
}
